import React, { useState, useEffect, useContext, createContext } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';

import useStoredState from '../hooks/useStoredState';
import { appName, defaultLocation, TODAY, fetchFromAcis } from '../utilities';

import { TabInfoContext } from './tab-info.context';

import biomassInfo from './max_biomasses.json';


async function getBaseData(selectedLocation, pastLocations) {
  const locInfo = pastLocations[selectedLocation];
  const coords = `${locInfo.lng},${locInfo.lat}`;

  const elems = {
    loc: coords,
    grid: 'nrcc-nn',
    sdate: '1951-01-01',
    edate: format(TODAY, 'yyyy-MM-dd'),
    elems: [
      { name: 'mint' },
      { name: 'maxt' },
      { name: 'pcpn' }
    ],
  };

  return fetchFromAcis(elems)
    .then(rawData => {
      const newRawChartData = {
        [selectedLocation]: {
          'mintMaxt': [],
          'pcpn': []
        }
      };

      rawData.forEach(([date, mint, maxt, pcpn]) => {
        newRawChartData[selectedLocation].mintMaxt.push([date, mint, maxt]);
        newRawChartData[selectedLocation].pcpn.push([date, pcpn]);
      });
      return newRawChartData;
    });
}

function constructTabNameFromPathname(pathname) {
  const validValues = ['Pests and Pathogens', 'Spring Freeze Damage', 'About'];
  let tabName = '';
  const parts = pathname.split('/');
  if (parts.length === 3) {
    const words = parts[1].split('-');
    const capitalizedWords = words.map(word => {
      if (['and'].includes(word)) {
        return word;
      } else {
        return word.slice(0,1).toUpperCase() + word.slice(1);
      }
    });
    tabName = capitalizedWords.join(' ');
  } else if (pathname === '/about') {
    tabName = 'About';
  }

  return validValues.includes(tabName) ? tabName : 'Home';
}

function constructSubTabNameFromPathname(pathname) {
  const parts = pathname.split('/');
  let words;
  if (parts.length === 3) {
    words = parts[2].split('-');
  } else if (parts.length === 2 && parts[1] !== '') {
    words = parts[1].split('-');
  } else {
    return 'none';
  }
  const capitalizedWords = words.map(word => {
    if (['and'].includes(word)) {
      return word;
    } else {
      return word.slice(0,1).toUpperCase() + word.slice(1);
    }
  });
  return capitalizedWords.join(' ');
}


// Set up initial state of context
export const GlobalContext = createContext({
  display: 'map',
  error: { message: '', show: false, error: null, extraInfo: {} },
  setError: () => {},
  toggleDisplay: () => {},
  selectedTab: '',
  selectedSubTab: '',
  overlayData: null,
  overlayDataDate: null,
  overlayColors: null,
  overlayBinColors: null,
  overlayBinThresholds: null,
  overlayFilename: null,
  updateOverlayData: () => {},
  isLoading: true,
  loading: [],
  chartData: {},
  handleUpdate: () => {},
  getSelectorValueByIdFromTabInformation: () => {},
  selectedLocation: null,
  pastLocations: {},
  rawChartData: {},
  biomassInfo: {}
});

// Set up context provider
export const GlobalProvider = ({ children }) => {
  const [display, setDisplay] = useState('map');
  const [loading, setLoading] = useState(['initialize']);
  const [error, setError] = useState({ message: '', show: false, error: null, extraInfo: {} });
  
  const [selectedTab, setSelectedTab] = useState(constructTabNameFromPathname(window.location.pathname));
  const [selectedSubTab, setSelectedSubTab] = useState(constructSubTabNameFromPathname(window.location.pathname));
  const [selectedLocation, setSelectedLocation] = useStoredState(`${appName}.selected-location`, defaultLocation.id);
  const [pastLocations, setPastLocations] = useStoredState(`${appName}.selected-past-locations`, { [defaultLocation.id]: defaultLocation }, true);

  const [overlayData, setOverlayData] = useState(null);
  const [overlayDataDate, setOverlayDataDate] = useState(null);
  const [overlayColors, setOverlayColors] = useState(null);
  const [overlayBinColors, setOverlayBinColors] = useState(null);
  const [overlayBinThresholds, setOverlayBinThresholds] = useState(null);
  const [overlayExtraLegendInfo, setOverlayExtraLegendInfo] = useState(null);
  const [overlayFilename, setOverlayFilename] = useState(null);

  const [rawChartData, setRawChartData] = useState({});
  const [chartData, setChartData] = useState({});

  const { tabInformation, tabsSharedState, updateTabFunctions, updateCurrentTabInformation, getSelectorValueFromTabInformation } = useContext(TabInfoContext);
  
  const addToLoading = (keys) => {
    setLoading((prev) => [...prev, ...keys]);
  };

  const removeFromLoading = (key) => {
    setLoading((currLoading) => {
      const newLoading = [...currLoading];
      const idx = newLoading.indexOf(key);
  
      if (idx > -1) {
        newLoading.splice(idx, 1);
      }  
      return newLoading;
    });
  };

  function toggleDisplay() {
    setDisplay(display === 'map' ? 'chart' : 'map');
  }

  function updateOverlayData(newOverlay) {
    handleUpdate('overlayDataDate', { newOverlayDataDate: newOverlay.dataDate });
    setOverlayData(newOverlay.data);
    setOverlayDataDate(newOverlay.dataDate);
    setOverlayColors(newOverlay.colors);
    setOverlayBinColors(newOverlay.binColors);
    setOverlayBinThresholds(newOverlay.binThresholds);
    setOverlayExtraLegendInfo(newOverlay.extraLegendInfo);
    setOverlayFilename(newOverlay.filename);
  }

  function getSelectorValueByIdFromTabInformation(id) {
    return getSelectorValueFromTabInformation(id, selectedTab, selectedSubTab);
  }

  async function handleUpdate(updateType, kwargs={}) {
    // Used to track what has changed for coordinating data updates
    // Defined outside of block to allow error objects access to it
    const whatChanged = [];

    try {
      // Used to delay state updates so that they all update at the same time, after data is fetched
      const stateUpdates = [];
  
      // Used to provide new state to update functions
      const updatedStateObjs = {
        selectedTab,
        selectedSubTab,
        tabInformation,
        tabsSharedState,
        selectedLocation,
        pastLocations,
        rawChartData,
        overlayDataDate,
        overlayFilename
      };
  
      switch (updateType) {
        case 'initialize':
          updatedStateObjs.tabsSharedState = updateTabFunctions.updateTabSharedStateOnChange('onInitialize', kwargs, updatedStateObjs);
          whatChanged.push('initialize');
          break;
        case 'nav':
          // Updates the shared state to reflect click on a new tab and/or subtab
          updatedStateObjs.tabsSharedState = updateTabFunctions.updateTabSharedStateOnChange('onNavChange', kwargs, updatedStateObjs);
      
          // Only update tab if it changed
          if (selectedTab !== kwargs['newTab']) {
            stateUpdates.push(() => setSelectedTab(kwargs['newTab']));
            whatChanged.push('selectedTab');
            updatedStateObjs.selectedTab = kwargs['newTab'];
          }
      
          // Only update subtab if it changed
          if (selectedSubTab !== kwargs['newSubTab']) {
            stateUpdates.push(async () => setSelectedSubTab(kwargs['newSubTab']));
            whatChanged.push('selectedSubTab');
            updatedStateObjs.selectedSubTab = kwargs['newSubTab'];
          }
  
          break;
        case 'addLocation':
          updatedStateObjs.tabsSharedState = updateTabFunctions.updateTabSharedStateOnChange('onLocationChange', kwargs, updatedStateObjs);
  
          stateUpdates.push(async () => setSelectedLocation(kwargs['newId']));
          whatChanged.push('selectedLocation');
          updatedStateObjs.selectedLocation = kwargs['newId'];
  
          stateUpdates.push(async () => setPastLocations(kwargs['newPastLocations']));
          whatChanged.push('pastLocations');
          updatedStateObjs.pastLocations = kwargs['newPastLocations'];
          break;
        case 'changeLocation':
          updatedStateObjs.tabsSharedState = updateTabFunctions.updateTabSharedStateOnChange('onLocationChange', kwargs, updatedStateObjs);
  
          stateUpdates.push(async () => setSelectedLocation(kwargs['newId']));
          whatChanged.push('selectedLocation');
          updatedStateObjs.selectedLocation = kwargs['newId'];
          break;
        case 'removeLocation':
          if (updatedStateObjs.selectedLocation !== kwargs['removeId']) {
            const newPastLocations = { ...updatedStateObjs.pastLocations };
            delete newPastLocations[kwargs['removeId']];
  
            stateUpdates.push(async () => setPastLocations(newPastLocations));
            whatChanged.push('pastLocations');
            updatedStateObjs.pastLocations = newPastLocations;
          }
          break;
        case 'tabOptions':
          // Updates tabInformation with changes to options
          updatedStateObjs.tabInformation = updateTabFunctions.updateTabOptions(kwargs);
          whatChanged.push(updatedStateObjs.tabInformation.find(tab => tab.name === updatedStateObjs.selectedTab).pageInfo[kwargs.subTabToUpdate][kwargs.pageToUpdate].options[kwargs.selectorIndexToUpdate].id);
          break;
        case 'tabSharedState':
          // Updates shared state with changes to options
          updatedStateObjs.tabsSharedState = updateTabFunctions.updateTabSharedState(kwargs);
          whatChanged.push(kwargs['sharedStateNameToEdit']);
          break;
        case 'rawChartData':
          whatChanged.push(updateType);
          break;
        case 'overlayDataDate':
          updatedStateObjs.overlayDataDate = kwargs['newOverlayDataDate'];
          whatChanged.push(updateType);
          break;
        default:
          console.error(`Invalid updateType provided: ${updateType}`);
          break;
      }
  
      if (whatChanged.includes('initialize') || whatChanged.includes('selectedTab')) {
        stateUpdates.push(() => updateCurrentTabInformation(updatedStateObjs));
      }

      // If location changes or this is the initial load, fetch base data which is mint, maxt, pcpn, gdd base 50, and gdd base 32.
      //   This makes switching between tabs faster at the cost of slightly longer initial load time.
      const dataStatePromises = [];
      let fetchingBaseData = false;
      if (whatChanged.includes('initialize') || whatChanged.includes('selectedLocation')) {
        dataStatePromises.push(async () => {
          //////////
          // This prevents refetching data that we already have if a previously selected location is clicked
          //  but still refreshed rawChartData so that we can use it changing as a trigger
          let baseData = {};
          if (!Object.keys(rawChartData).includes(updatedStateObjs.selectedLocation)) {
            baseData = await getBaseData(updatedStateObjs.selectedLocation, updatedStateObjs.pastLocations);
          }
          setRawChartData({...rawChartData, ...baseData});
          //////////

          removeFromLoading('getBaseData');
        });
        addToLoading(['getBaseData']);
        fetchingBaseData = true;
      }
  
      // Get information about how and what to update
      const onChangeArray = updatedStateObjs.tabInformation.find(tab => tab.name === updatedStateObjs.selectedTab).onChange;
  
      // Iterate what has changed and what changes should trigger updates, pushing handler functions to array
      for (const { whenXChanges, handleChange, showLoading } of onChangeArray) {
        for (const changedName of whatChanged) {
          if (whenXChanges.includes(changedName) || changedName === 'initialize') {
            if (showLoading) {
              stateUpdates.push(() => removeFromLoading(changedName));
              addToLoading([changedName]);
            }
            dataStatePromises.push(handleChange({ ...updatedStateObjs, biomassInfo }, kwargs, { updateOverlayData, setRawChartData, setChartData, getSelectorValueByIdFromTabInformation, fetchingBaseData, updateCurrentTabInformation: (updateObj) => updateCurrentTabInformation({...updatedStateObjs, ...updateObj}) }));
            break;
          }
        }
      }
  
      // Wait for all data fetching function to resolve. Each should return a Promise that, when resolved, returns a setState function
      const dataStateUpdates = await Promise.all(dataStatePromises);
  
      // Add all of the setState functions together and execute them all
      dataStateUpdates.concat(stateUpdates).forEach(func => func());
    } catch (e) {
      console.error(e);
      setLoading([]);
      setError({
        message: e.message,
        show: true,
        error: e,
        extraInfo: { whatChanged }
      });
    }
  }

  useEffect(() => {
    if (tabInformation.length && loading.filter(n => n === 'initialize').length === 1) {
      handleUpdate('initialize');
      removeFromLoading('initialize');
    }
  }, [tabInformation]);

  useEffect(() => {
    if (Object.keys(rawChartData).length) {
      handleUpdate('rawChartData');
    }
  }, [rawChartData]);

  useEffect(() => {
    window.onpopstate = () => {
      const newTab = constructTabNameFromPathname(window.location.pathname);
      const newSubTab = constructSubTabNameFromPathname(window.location.pathname);
      handleUpdate('nav', { newTab, newSubTab });
    }
  });

  const value = {
    display,
    error,
    setError,
    toggleDisplay,
    selectedTab,
    selectedSubTab,
    overlayData,
    overlayDataDate,
    overlayColors,
    overlayBinColors,
    overlayBinThresholds,
    overlayExtraLegendInfo,
    overlayFilename,
    updateOverlayData,
    isLoading: loading.length > 0,
    loading,
    chartData,
    handleUpdate,
    getSelectorValueByIdFromTabInformation,
    selectedLocation: pastLocations[selectedLocation],
    pastLocations,
    rawChartData: selectedLocation in rawChartData ? rawChartData[selectedLocation] : {},
    biomassInfo
  };
  return (
    <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>
  );
};

GlobalProvider.propTypes = {
  children: PropTypes.node,
};
